import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";

type FunnelData = {
  totalLeads: number;
  totalDelivered: number;
  totalRead: number;
  totalResponded: number;
  totalAppointmentIntention: number;
  totalAppointments: number;
  totalTransfers: number;
  totalDiscarded: number;
  totalInactive: number;
  totalLeadsPercentage: number;
  deliveredPercentage: number;
  readPercentage: number;
  respondedPercentage: number;
  appointmentIntentionPercentage: number;
  appointmentsPercentage: number;
  transfersPercentage: number;
  discardedPercentage: number;
  inactivePercentage: number;
};

type FunnelCardProps = {
  data: FunnelData | null;
  colSpan?: number;
  isLoading?: boolean;
};

type ChartDataItem = {
  name: string;
  value: number;
  nominalValue: number;
  percentage: number;
  variant: "primary" | "success" | "danger";
};

const HorizontalBar = ({ item, maxValue }: { item: ChartDataItem; maxValue: number }) => {
  const barWidth = maxValue === 0 ? 0 : (item.value / maxValue) * 100;

  const barColorClass = {
    primary: "bg-palomma-primary",
    success: "bg-green-500",
    danger: "bg-red-500",
  }[item.variant];

  return (
    <div className="group relative">
      <div className="flex items-center h-12 mb-2 cursor-pointer transition-all duration-200 hover:scale-[1.01]">
        <div className="w-32 md:w-40 pr-3 shrink-0">
          <p className="text-sm font-medium text-gray-600 text-right leading-tight">{item.name}</p>
        </div>

        <div className="flex-1 relative h-8 flex items-center min-w-0">
          <div className="h-8 w-full bg-gray-100 rounded-md relative overflow-hidden">
            <div
              className={`h-full rounded-md transition-all duration-500 relative flex items-center justify-end pr-2 ${barColorClass}`}
              style={{ width: `${barWidth}%` }}
            >
              {barWidth > 15 && (
                <span className="text-xs font-semibold text-white drop-shadow-sm">
                  {item.percentage.toFixed(1)}%
                </span>
              )}
            </div>
          </div>

          {barWidth <= 15 && (
            <span
              className="text-xs font-medium text-gray-600 absolute whitespace-nowrap"
              style={{ left: `calc(${barWidth}% + 8px)` }}
            >
              {item.percentage.toFixed(1)}%
            </span>
          )}

          <div className="ml-3 shrink-0 min-w-[60px]">
            <p className="text-sm font-semibold text-gray-700">{item.nominalValue.toLocaleString()}</p>
          </div>
        </div>
      </div>
      
      {/* Tooltip */}
      <div className="absolute left-full ml-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 whitespace-nowrap shadow-lg">
        <p className="font-semibold mb-0.5">{item.name}</p>
        <p>{item.nominalValue.toLocaleString()} contactos</p>
        <p>{item.percentage.toFixed(1)}% del total</p>
      </div>
    </div>
  );
};

const FunnelCard = ({ data, colSpan = 1, isLoading = false }: FunnelCardProps) => {
  if (isLoading) {
    return (
      <Card className={colSpan > 1 ? `col-span-${colSpan}` : ""}>
        <CardContent className="h-[300px] flex items-center justify-center">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card className={colSpan > 1 ? `col-span-${colSpan}` : ""}>
        <CardContent className="h-[300px] flex items-center justify-center">
          <p className="text-gray-500">No hay datos disponibles</p>
        </CardContent>
      </Card>
    );
  }

  const hasEarlyStageData = data.totalLeads > 0 && data.totalDelivered > 0 && data.totalRead > 0;
  const baseline = hasEarlyStageData ? data.totalLeads : data.totalAppointments;

  const calculateValue = (value: number) => {
    if (baseline === 0) return 0;
    return (value / baseline) * 100;
  };

  const calculatePercentage = (value: number, originalPercentage: number) => {
    if (hasEarlyStageData) return originalPercentage;
    if (baseline === 0) return 0;
    return (value / baseline) * 100;
  };

  const chartData: ChartDataItem[] = [
    {
      name: "Total Contactos",
      value: hasEarlyStageData ? 100 : calculateValue(data.totalLeads),
      nominalValue: data.totalLeads,
      percentage: hasEarlyStageData ? 100 : calculatePercentage(data.totalLeads, 100),
      variant: "primary",
    },
    {
      name: "Se enviaron",
      value: calculateValue(data.totalDelivered),
      nominalValue: data.totalDelivered,
      percentage: hasEarlyStageData
        ? data.deliveredPercentage
        : calculatePercentage(data.totalDelivered, data.deliveredPercentage),
      variant: "primary",
    },
    {
      name: "Leyeron",
      value: calculateValue(data.totalRead),
      nominalValue: data.totalRead,
      percentage: hasEarlyStageData
        ? data.readPercentage
        : calculatePercentage(data.totalRead, data.readPercentage),
      variant: "primary",
    },
    {
      name: "Respondieron",
      value: calculateValue(data.totalResponded),
      nominalValue: data.totalResponded,
      percentage: hasEarlyStageData
        ? data.respondedPercentage
        : calculatePercentage(data.totalResponded, data.respondedPercentage),
      variant: "success",
    },
    {
      name: "Intención de cita",
      value: calculateValue(data.totalAppointmentIntention),
      nominalValue: data.totalAppointmentIntention,
      percentage: calculatePercentage(data.totalAppointmentIntention, data.appointmentIntentionPercentage),
      variant: "success",
    },
    {
      name: "Agendaron",
      value: calculateValue(data.totalAppointments),
      nominalValue: data.totalAppointments,
      percentage: calculatePercentage(data.totalAppointments, data.appointmentsPercentage),
      variant: "success",
    },
    {
      name: "Transferidos",
      value: calculateValue(data.totalTransfers),
      nominalValue: data.totalTransfers,
      percentage: calculatePercentage(data.totalTransfers, data.transfersPercentage),
      variant: "success",
    },
    {
      name: "Descartados",
      value: calculateValue(data.totalDiscarded),
      nominalValue: data.totalDiscarded,
      percentage: calculatePercentage(data.totalDiscarded, data.discardedPercentage),
      variant: "danger",
    },
    {
      name: "Inactivos",
      value: calculateValue(data.totalInactive),
      nominalValue: data.totalInactive,
      percentage: calculatePercentage(data.totalInactive, data.inactivePercentage),
      variant: "danger",
    },
  ];

  const maxValue = Math.max(...chartData.map((item) => item.value));

  return (
    <Card className={colSpan > 1 ? `col-span-${colSpan}` : ""}>
      <CardContent className="py-6">
        <div className="flex flex-col gap-2">
          <h3 className="text-lg font-semibold text-gray-900 text-center">
            Resumen General del Funnel
          </h3>

          <div className="py-4 space-y-1">
            {chartData.map((item, index) => (
              <HorizontalBar key={index} item={item} maxValue={maxValue} />
            ))}
          </div>

          <p className="text-xs text-gray-500 text-center mt-2">
            Porcentaje de contactos por etapa del funnel
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default FunnelCard;
